import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, CheckCircle2, Lightbulb, Send } from 'lucide-react';
import { toast } from 'sonner';
import { proposalsApi } from '@/api/proposals';
import { unwrapError } from '@/api/client';
import type { ActionDecision, ChatTurnUsage } from '@/api/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

export function DecisionCard({
  decision,
  usage,
  latencyMs,
  sessionId,
}: {
  decision: ActionDecision;
  usage: ChatTurnUsage;
  latencyMs: number;
  sessionId: string;
}): JSX.Element {
  const { t } = useTranslation();
  const variantByKind = {
    resolved: 'success' as const,
    proposal: 'warning' as const,
    escalated: 'secondary' as const,
    failed: 'destructive' as const,
  };
  const iconByKind = {
    resolved: CheckCircle2,
    proposal: Lightbulb,
    escalated: Send,
    failed: AlertCircle,
  };
  const Icon = iconByKind[decision.kind];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2 pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Icon className="h-4 w-4" />
          {t('chat.decision.title')} <Badge variant={variantByKind[decision.kind]}>{decision.kind}</Badge>
        </CardTitle>
        <div className="text-[11px] text-muted-foreground">
          {t('chat.decision.tokensLatency', {
            tokens: usage.inputTokens + usage.outputTokens,
            ms: latencyMs,
          })}
        </div>
      </CardHeader>
      <CardContent>
        {decision.kind === 'resolved' ? (
          <div className="prose prose-sm max-w-none whitespace-pre-wrap rounded-md border bg-muted/30 p-3 text-sm dark:prose-invert">
            {decision.answer}
          </div>
        ) : decision.kind === 'proposal' ? (
          <div className="space-y-3">
            <p className="text-sm">{decision.summary}</p>
            <details className="rounded-md border bg-muted/20 p-2 text-xs">
              <summary className="cursor-pointer font-medium">
                {t('chat.decision.payload', { tool: decision.proposal.toolName })}
              </summary>
              <pre className="mt-2 overflow-auto text-[11px]">
                {JSON.stringify(decision.proposal.payload, null, 2)}
              </pre>
            </details>
            <ProposalActions proposalId={decision.proposal.proposalId} />
          </div>
        ) : decision.kind === 'escalated' ? (
          <div className="space-y-1 text-sm">
            <p>{decision.summary}</p>
            <p className="font-mono text-xs text-muted-foreground">
              {t('chat.decision.zohoTicket', { id: decision.ticketId })}
            </p>
          </div>
        ) : (
          <p className="text-sm text-destructive">{decision.reason}</p>
        )}
        <p className="mt-3 font-mono text-[10px] text-muted-foreground">
          {t('chat.decision.session', { id: sessionId })}
        </p>
      </CardContent>
    </Card>
  );
}

function ProposalActions({ proposalId }: { proposalId: string }): JSX.Element {
  const { t } = useTranslation();
  const [busy, setBusy] = React.useState(false);
  const [done, setDone] = React.useState<'confirmed' | 'rejected' | null>(null);

  const confirm = async (): Promise<void> => {
    setBusy(true);
    try {
      const res = await proposalsApi.confirm(proposalId);
      if (res.status === 'executed') toast.success(t('chat.decision.executed'));
      else if (res.status === 'failed')
        toast.error(t('chat.decision.executionFailed', { error: res.error ?? '—' }));
      else toast.info(t('chat.decision.statusInfo', { status: res.status }));
      setDone('confirmed');
    } catch (err) {
      toast.error(unwrapError(err));
    } finally {
      setBusy(false);
    }
  };
  const reject = async (): Promise<void> => {
    setBusy(true);
    try {
      await proposalsApi.reject(proposalId);
      toast.success(t('chat.decision.rejectedToast'));
      setDone('rejected');
    } catch (err) {
      toast.error(unwrapError(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <p className="text-xs text-muted-foreground">
        {t('chat.decision.proposalDone', { state: t(`chat.decision.${done}`) })}
      </p>
    );
  }

  return (
    <div className="flex gap-2">
      <Button size="sm" disabled={busy} onClick={confirm}>
        {t('chat.decision.confirm')}
      </Button>
      <Button size="sm" variant="outline" disabled={busy} onClick={reject}>
        {t('chat.decision.reject')}
      </Button>
    </div>
  );
}
